import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, RotateCcw, Sparkles, PauseCircle, Eye, GitBranch } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, formatErr } from "../../lib/api";
import { btn } from "../leads/PriorityBadge";

const nextMonday = () => {
  const d = new Date(); d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const PublishDialog = ({ r, open, onOpenChange, onDone }) => {
  const [start, setStart] = useState(nextMonday());
  const [replace, setReplace] = useState(true);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/plan-reviews/${r.id}/approve-publish`, { start_date: start, confirm: true, replace_future_sessions: replace });
      toast.success(`Publicada: ${data.sessions} sesiones${data.activated ? " · cliente activado" : ""}`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" style={{ background: "#0a0a0a", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }} data-testid="pr-publish-dialog">
        <DialogHeader><DialogTitle style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 26 }}>Aprobar y publicar</DialogTitle></DialogHeader>
        <div style={{ fontSize: 13, color: "#d4d4d4", display: "flex", flexDirection: "column", gap: 12 }}>
          {(r.alerts || []).filter((a) => a.level !== "info").map((a, i) => (
            <div key={i} style={{ color: a.level === "high" ? "#f87171" : "#facc15", fontSize: 12 }} data-testid={`pr-publish-alert-${i}`}>⚠ {a.text}</div>
          ))}
          <p>El futbolista verá esta versión en su área y las sesiones aparecerán en su calendario.{r.user?.role === "prospect" && " Su cuenta se activará como cliente."}</p>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>Fecha de inicio (lunes recomendado)
            <input type="date" className="qv-input" value={start} onChange={(e) => setStart(e.target.value)} data-testid="pr-publish-start" /></label>
          {r.published_plan_id && (
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="pr-publish-replace" />
              Quitar del calendario las sesiones futuras no realizadas de la versión anterior (desde la fecha de inicio). La versión anterior queda en el historial.
            </label>
          )}
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="pr-publish-confirm" />
            He revisado la planificación y la apruebo expresamente.
          </label>
          <button className="qv-btn-primary" disabled={!ok || busy || !start} onClick={go} style={{ justifyContent: "center" }} data-testid="pr-publish-submit">
            {busy ? "Publicando…" : "Aprobar y publicar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ActionsBar = ({ r, onChange }) => {
  const [pub, setPub] = useState(false);
  const published = r.status === "published";
  const formOpen = r.onboarding && ["pending", "in_progress"].includes(r.onboarding.status);
  const call = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); onChange(); } catch (e) { toast.error(formatErr(e)); } };
  const setStatus = (status, label) => {
    const reason = status === "info_needed" ? window.prompt("¿Qué falta o qué hay que corregir? (opcional)") : null;
    if (reason === null && status === "info_needed") return;
    call(() => api.patch(`/admin/plan-reviews/${r.id}/status`, { status, reason: reason || null }), label);
  };
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }} data-testid="pr-actions">
      {published ? (
        <button style={btn("#dc2626")} onClick={() => window.confirm("Se creará una nueva versión editable. El cliente seguirá viendo la publicada hasta que apruebes la nueva.") && call(() => api.post(`/admin/plan-reviews/${r.id}/new-version`), "Nueva versión creada")} data-testid="pr-new-version"><GitBranch size={14} /> Modificar (nueva versión)</button>
      ) : (<>
        <button style={btn("transparent")} disabled={formOpen || r.status === "draft_generating"} title={formOpen ? "El formulario aún no está completo" : ""}
          onClick={() => (!r.plan?.day_count || window.confirm("La IA sustituirá el borrador actual (se guarda antes una copia en Versiones). ¿Continuar?")) && call(() => api.post(`/admin/plan-reviews/${r.id}/generate`), "Generando borrador con IA… puede tardar unos minutos")} data-testid="pr-generate"><Sparkles size={14} /> Generar borrador con IA</button>
        <button style={btn("transparent")} onClick={() => setStatus("coach_review", "En revisión")} data-testid="pr-to-review"><Eye size={14} /> Marcar en revisión</button>
        <button style={btn("transparent")} onClick={() => setStatus("info_needed", "Marcado pendiente de información")} data-testid="pr-to-info-needed"><PauseCircle size={14} /> Pendiente de información</button>
        <button style={btn("transparent")} onClick={() => setStatus("draft_ready", "Devuelto a borrador")} data-testid="pr-to-draft"><RotateCcw size={14} /> Devolver a borrador</button>
        <button style={btn("#16a34a")} disabled={!r.plan || !r.plan.day_count} title={!r.plan?.day_count ? "Añade al menos un día de entrenamiento" : ""} onClick={() => setPub(true)} data-testid="pr-approve"><CheckCircle2 size={14} /> Aprobar y publicar</button>
      </>)}
      <PublishDialog r={r} open={pub} onOpenChange={setPub} onDone={onChange} />
    </div>
  );
};
